import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, logoUrl, faviconUrl } from "@/lib/api";

const STATIC_MODE = process.env.REACT_APP_STATIC_CONTENT === 'true';

const LINKS = [
  { href: "#servicios",    label: "Servicios" },
  { href: "#broadcasting", label: "Broadcasting" },
  { href: "#nosotros",     label: "Nosotros" },
  { href: "#galeria",      label: "Trabajos" },
  { href: "#clientes",     label: "Clientes" },
  { href: "#contacto",     label: "Contacto" },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);

  const [brandData, setBrandData]       = useState(null);
  const [brandLoading, setBrandLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        let brand;
        if (STATIC_MODE) {
          const res  = await fetch('/data/content.json');
          const json = await res.json();
          brand = json?.content?.brand;
        } else {
          const res = await api.get("/public/home");
          brand = res.data?.content?.brand;
        }
        setBrandData(brand ?? null);
      } catch (e) {
        console.error("NavBar: error cargando brand", e);
      } finally {
        setBrandLoading(false);
      }
    };
    load();
  }, []);

  const logoFilename    = brandData?.logo_filename;
  const faviconFilename = brandData?.favicon_filename;
  const logoScale       = brandData?.logo_scale    ?? 1.0;
  const logoOffsetX     = brandData?.logo_offset_x ?? 0;
  const logoOffsetY     = brandData?.logo_offset_y ?? 0;

  // Scroll handler
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Favicon dinámico — actualiza <link rel="icon"> cuando el admin sube uno
  useEffect(() => {
    if (!faviconFilename) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl(faviconFilename);
  }, [faviconFilename]);

  return (
    <>
      <header
        className={`fixed top-[2px] left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "py-3 bg-overlay backdrop-blur-md border-b border-line-2" : "py-5 bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-6">

          {/* ── Brand link ── */}
          <a
            href="#top"
            className="flex items-center gap-3 group"
            style={{ position: 'relative', flexShrink: 0 }}
          >
            {/* Slot fijo 150×36px */}
            <div style={{ position: 'relative', width: 150, height: 36, display: 'flex', alignItems: 'center' }}>
              <BrandMark logoFilename={logoFilename} isLoading={brandLoading} scale={logoScale} offsetX={logoOffsetX} offsetY={logoOffsetY} />
            </div>

            {/* Brand text (only when no logo image AND data already loaded) */}
            {!brandLoading && !logoFilename && (
              <div className="leading-tight" style={{ flexShrink: 0 }}>
                <div className="text-fg font-semibold text-[17px] tracking-tight">
                  {brandData?.name || "LUVA Solutions"}
                </div>
                <div className="text-mute text-[11px] tracking-wide">IT · Broadcasting</div>
              </div>
            )}
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} className="text-fg-2 hover:text-fg text-[14px] font-medium transition-colors">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href="#contacto" className="hidden sm:inline-flex btn-solid items-center gap-1.5 px-4 py-2 text-[13px] rounded-sm">
              Hablar con LUVA <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <button
              className="lg:hidden w-10 h-10 border border-line flex items-center justify-center text-fg"
              onClick={() => setOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[55] bg-black/85 lg:hidden" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            className="absolute right-0 top-0 bottom-0 w-80 bg-surface p-6 border-l border-line"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="text-fg font-semibold">Menú</div>
              <button onClick={() => setOpen(false)} className="w-9 h-9 border border-line flex items-center justify-center text-fg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-1">
              {LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block px-3 py-3 border-b border-line-2 text-fg-2 hover:text-fg">
                  {l.label}
                </a>
              ))}
            </div>
            <a href="#contacto" onClick={() => setOpen(false)} className="mt-6 btn-solid w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm rounded-sm">
              Hablar con LUVA
            </a>
            <Link to="/admin" className="mt-6 block text-faint text-xs hover:text-fg">/admin →</Link>
          </motion.div>
        </div>
      )}
    </>
  );
}

function BrandMark({ logoFilename, isLoading, scale = 1.0, offsetX = 0, offsetY = 0 }) {
  const [imgError, setImgError] = useState(false);

  if (isLoading) {
    return <div style={{ width: 36, height: 36 }} />;
  }
  if (logoFilename && !imgError) {
    return (
      <img
        src={logoUrl(logoFilename)}
        alt="LUVA Solutions"
        className="h-9 w-auto max-w-[140px]"
        style={{
          objectFit:       'contain',
          transform:       `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
          transformOrigin: 'center center',
        }}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className="relative w-9 h-9 bg-brand flex items-center justify-center overflow-hidden" style={{ borderRadius: 2 }}>
      <span className="text-white font-bold text-[15px] tracking-tighter">LU</span>
    </div>
  );
}
